import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Footer from '../../../components/feature/Footer';
import StructuredData from '../../../components/feature/StructuredData';
import Button from '../../../components/base/Button';

interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  date: string;
  image: string;
  slug: string;
  content: React.ReactNode;
}

const BlogArticle: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://hudosan-shobun.com";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleFormClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17931041617/eH8qCOvAwPIbENH2l-ZC',
        'value': 1.0,
        'currency': 'JPY'
      });
    }
  };

  const handleLineClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17931041617/eH8qCOvAwPIbENH2l-ZC',
        'value': 1.0,
        'currency': 'JPY'
      });
    }
  };

  const handleMailtoClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17931041617/eH8qCOvAwPIbENH2l-ZC',
        'value': 1.0,
        'currency': 'JPY'
      });
    }
  };

  const articles: { [key: string]: Article } = {
    'sanrin-syobun-5-houhou': {
      id: '1',
      title: '相続した山林がいらない…愛知県で処分する5つの方法',
      description: '相続した山林を処分する5つの方法を弁護士が解説。隣接地所有者への譲渡、相続土地国庫帰属制度の活用、自治体への寄付など具体的な手続きと費用を紹介。愛知・岐阜・三重・静岡対応。初回相談無料。',
      category: '山林・原野',
      tags: ['山林', '相続', '処分', '愛知県', '固定資産税'],
      date: '2026-01-15',
      image: 'https://public.readdy.ai/ai/img_res/cde2d2685bd0768957668028f9f77bfd.jpg',
      slug: 'sanrin-syobun-5-houhou',
      content: (
        <>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            「親から山林を相続したけれど、管理もできないし固定資産税だけがかかり続ける…」<br />
            「売ろうにも買い手が見つからない」<br />
            「自治体が山林の処分を断った」
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            このような悩みを抱えている方は少なくありません。愛知県内でも、相続した山林の処分に困っている方が増えています。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            本記事では、相続した山林を処分する5つの方法について、それぞれのメリット・デメリット、具体的な手続きや費用を詳しく解説します。
          </p>

          {/* 目次 */}
          <nav className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-12">
            <h2 className="font-noto text-xl font-bold text-navy-900 mb-4 flex items-center">
              <i className="ri-list-unordered text-navy-600 text-2xl mr-2"></i>
              目次
            </h2>
            <ol className="space-y-3 font-noto text-navy-700">
              <li>
                <a href="#method-1" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">1.</span>
                  <span>隣接地所有者への譲渡</span>
                </a>
              </li>
              <li>
                <a href="#method-2" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">2.</span>
                  <span>相続土地国庫帰属制度の活用</span>
                </a>
              </li>
              <li>
                <a href="#method-3" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">3.</span>
                  <span>自治体・公益法人への寄付</span>
                </a>
              </li>
              <li>
                <a href="#method-4" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">4.</span>
                  <span>事業者への譲渡（太陽光発電・資材置き場等）</span>
                </a>
              </li>
              <li>
                <a href="#method-5" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">5.</span>
                  <span>相続放棄（相続前の場合）</span>
                </a>
              </li>
              <li>
                <a href="#summary" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">6.</span>
                  <span>まとめ：弁護士に相談するメリット</span>
                </a>
              </li>
            </ol>
          </nav>

          <h2 id="method-1" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            1. 隣接地所有者への譲渡
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            最も現実的で成功率が高い方法が、<strong>隣接地所有者への譲渡</strong>です。
          </p>
          
          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            なぜ隣接地所有者なのか
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            隣接地の所有者には、以下のような理由で山林を引き受けるメリットがあります。
          </p>
          <div className="bg-beige-50 border-l-4 border-navy-600 p-6 mb-8">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>自分の土地を広げられる（将来の利用可能性が広がる）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>境界が明確で測量費用を抑えられる</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>管理がしやすい（既に隣接地を管理しているため）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>無償または低価格でも受け入れてもらえる可能性がある</span>
              </li>
            </ul>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            隣接地所有者の探し方
          </h3>
          
          <div className="space-y-6 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">①</span>
                法務局で登記事項証明書を取得
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                法務局で対象の山林の「公図」を取得し、隣接する土地の地番を確認します。その地番の「登記事項証明書」を取得すれば、所有者の氏名と住所がわかります。費用は、公図が1通450円、登記事項証明書が1通600円です。
              </p>
            </div>

            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">②</span>
                市区町村の固定資産税課に相談
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                固定資産税の課税台帳を閲覧することで、隣接地所有者を調べることもできます。
              </p>
            </div>

            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">③</span>
                地方の森林組合に相談
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                森林組合は地域の山林所有者と接点があるため、隣接地所有者の連絡先を知っている場合があります。
              </p>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            譲渡の際の注意点
          </h3>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-file-text-line text-yellow-600 text-xl mr-2"></i>
              契約書の作成
            </h4>
            <p className="font-noto text-navy-700 mb-3">
              口頭での約束ではなく、必ず書面で契約を締結してください。以下の事項を明記します。
            </p>
            <ul className="space-y-2 font-noto text-navy-700 ml-6">
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span>譲渡する土地の所在・地番・地目・面積</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span>譲渡の対価（無償の場合は「無償譲渡」と明記）</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span>境界の状態（「現況有姿」で譲渡する場合はその旨を明記）</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span>瑕疵担保責任（契約不適合責任）の有無</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span>所有権移転登記の手続きと費用負担</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-12">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-money-yen-circle-line text-blue-600 text-xl mr-2"></i>
              税金の問題
            </h4>
            <p className="font-noto text-navy-700">
              無償で譲渡した場合、受け取った側に「贈与税」がかかる可能性があります。ただし、山林の評価額が110万円以下であれば、贈与税の基礎控除の範囲内となり、課税されません。
            </p>
          </div>

          <h2 id="method-2" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            2. 相続土地国庫帰属制度の活用
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            2023年4月27日に始まった<strong>「相続土地国庫帰属制度」</strong>を利用すれば、一定の要件を満たす土地を国に引き取ってもらうことができます。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            制度の概要
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            この制度は、相続または遺贈によって取得した土地を、審査手数料と負担金を納付することで国庫に帰属させる（国に引き取ってもらう）ものです。詳細は<a href="https://www.moj.go.jp/MINJI/minji05_00454.html" target="_blank" rel="noopener noreferrer" className="text-navy-600 underline hover:text-navy-800">法務省のウェブサイト</a>をご確認ください。
          </p>

          <div className="bg-white border border-beige-200 rounded-lg p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-4 flex items-center">
              <i className="ri-user-line text-navy-600 mr-2"></i>
              申請できる人
            </h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>相続または相続人に対する遺贈によって土地を取得した人</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>共有地の場合は、共有者全員で申請する必要あり</span>
              </li>
            </ul>
            <p className="font-noto text-navy-700 mt-4 text-sm">
              ※売買や贈与で取得した土地、法人が所有する土地は対象外です。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            申請できない土地（却下事由）
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            以下のいずれかに該当する土地は、申請の段階で却下されます。
          </p>
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>建物がある土地（更地にする必要あり）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>担保権や使用収益権が設定されている土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>通路など他人による使用が予定されている土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>土壌汚染がある土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>境界が明らかでない土地、所有権の争いがある土地</span>
              </li>
            </ul>
          </div>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-yellow-600 text-xl mr-2"></i>
              境界について重要な補足
            </h4>
            <p className="font-noto text-navy-700">
              特に「境界が明らかでない土地」は重要です。山林は境界が不明確なことが多いため、申請前に境界を明らかにする必要があります。ただし、必ずしも測量が必要というわけではなく、隣接地所有者との間で境界について争いがなく、法務局の審査で境界が特定できれば足りるとされています。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            費用
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-4 flex items-center">
                <i className="ri-file-list-line text-navy-600 mr-2"></i>
                審査手数料
              </h4>
              <p className="font-noto text-2xl font-bold text-navy-900 mb-2">
                14,000円<span className="text-base font-normal text-navy-600">/筆</span>
              </p>
              <p className="font-noto text-navy-700 text-sm">
                申請時に収入印紙で納付します。却下・不承認となった場合でも返還されません。
              </p>
            </div>

            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-4 flex items-center">
                <i className="ri-money-yen-circle-line text-navy-600 mr-2"></i>
                負担金
              </h4>
              <p className="font-noto text-navy-700 text-sm mb-2">
                承認された場合、国に「負担金」を納付します。山林の場合、面積に応じて算定されます。
              </p>
              <p className="font-noto text-navy-700 text-sm">
                通知から30日以内に納付
              </p>
            </div>
          </div>

          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-8">
            <p className="font-noto text-navy-700 mb-4">
              山林の負担金は面積に応じて算定されます。詳細な金額は法務省のウェブサイトで自動計算シートが提供されています。
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="font-noto text-navy-700 text-sm">
                <strong>特例：</strong>隣接する2筆以上の山林を申請する場合、合計面積で負担金を算定できる特例があります。
              </p>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            手続きの流れ
          </h3>
          <div className="space-y-4 mb-12">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">1</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">事前相談</h4>
                  <p className="font-noto text-navy-700 text-sm">法務局本局で受付</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">2</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">承認申請書の提出</h4>
                  <p className="font-noto text-navy-700 text-sm">審査手数料の納付</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">3</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">法務局による審査</h4>
                  <p className="font-noto text-navy-700 text-sm">書面審査・実地調査（半年から1年程度）</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">4</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">承認・不承認の通知</h4>
                  <p className="font-noto text-navy-700 text-sm">審査結果の通知</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">5</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">負担金の納付</h4>
                  <p className="font-noto text-navy-700 text-sm">通知から30日以内</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">6</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">国庫帰属</h4>
                  <p className="font-noto text-navy-700 text-sm">負担金納付時に所有権移転（登記は国が行います）</p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            弁護士が行うこと
          </h3>
          <ul className="space-y-3 font-noto text-navy-700 mb-12 ml-6">
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>要件を満たすかの事前調査</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>必要書類の収集・作成</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>法務局への申請代行</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>不承認の場合の代替案提案</span>
            </li>
          </ul>

          <h2 id="method-3" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            3. 自治体・公益法人への寄付
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            市町村や公益法人に寄付する方法もありますが、<strong>実際には受け入れてもらえないケースが大半</strong>です。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            受け入れてもらえる可能性があるケース
          </h3>
          <div className="bg-beige-50 border-l-4 border-navy-600 p-6 mb-8">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>公共事業（道路拡張等）に必要な土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>公園・緑地として活用できる土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>防災上重要な土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>環境保全上重要な森林</span>
              </li>
            </ul>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            受け入れてもらえない理由
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            自治体が山林の寄付を断る主な理由は以下のとおりです。
          </p>
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span><strong>管理費用がかかる</strong>（草刈り、倒木処理、不法投棄対応など）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span><strong>利用計画がない</strong></span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span><strong>固定資産税収入が減少する</strong></span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span><strong>責任問題</strong>（土砂災害、倒木事故など）</span>
              </li>
            </ul>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            特に近年は、自治体の財政状況が厳しく、管理コストがかかる山林の寄付を受け入れる余裕がないケースが増えています。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相談先
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            寄付を検討する場合は、まず市区町村の<strong>財産管理課</strong>や<strong>管財課</strong>に相談してください。愛知県内の山林であれば、県の<strong>農林水産事務所</strong>に相談することもできます。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            弁護士が行うこと
          </h3>
          <ul className="space-y-3 font-noto text-navy-700 mb-12 ml-6">
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>自治体・公益法人への打診</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>寄付の公益性を説明する資料作成</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>寄付契約書の作成</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>断られた場合の代替案提案</span>
            </li>
          </ul>

          <h2 id="method-4" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            4. 事業者への譲渡（太陽光発電・資材置き場等）
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            山林でも、<strong>太陽光発電事業者や資材置き場を探している事業者</strong>にとっては価値がある場合があります。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            譲渡先候補
          </h3>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-sun-line text-yellow-600 text-xl mr-2"></i>
                太陽光発電事業者
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                日当たりが良く、送電線に近い山林は太陽光発電に適している場合があります。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-truck-line text-orange-600 text-xl mr-2"></i>
                建設・運送業者
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                資材置き場や車両置き場として活用できる平坦な山林は需要があります。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-seedling-line text-green-600 text-xl mr-2"></i>
                林業事業者
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                木材として価値がある樹木が生えている山林は、林業事業者が引き取る可能性があります。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-recycle-line text-teal-600 text-xl mr-2"></i>
                産業廃棄物処理業者
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                広大な山林は、適法な産業廃棄物処理施設の候補地となる場合があります。
              </p>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            探し方
          </h3>
          <div className="space-y-4 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm">1</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">地元の不動産会社に相談</h4>
                  <p className="font-noto text-navy-700 text-sm">地域の事情に詳しい不動産会社は、事業者とのネットワークを持っている場合があります。</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm">2</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">森林組合に相談</h4>
                  <p className="font-noto text-navy-700 text-sm">林業事業者とのつながりがあり、木材価値のある山林の引き取り先を紹介してもらえることがあります。</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm">3</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">専門業者に問い合わせ</h4>
                  <p className="font-noto text-navy-700 text-sm">「山林売買」「山林引き取り」などで検索し、専門業者に問い合わせてみましょう。</p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            注意点
          </h3>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-alert-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span><strong>違法な開発や廃棄物投棄に利用されないよう、譲渡先の信頼性を確認する</strong></span>
              </li>
              <li className="flex items-start">
                <i className="ri-alert-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>契約書に土地の現状を明記し、後のトラブルを防ぐ</span>
              </li>
              <li className="flex items-start">
                <i className="ri-alert-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>開発許可等の手続きは、原則として譲受人が行う</span>
              </li>
            </ul>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            弁護士が行うこと
          </h3>
          <ul className="space-y-3 font-noto text-navy-700 mb-12 ml-6">
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>土地の特性調査（日照、アクセス、地形等）</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>事業者へのアプローチ・交渉</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>譲渡契約書の作成</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>法令上の制約（開発許可等）の確認</span>
            </li>
          </ul>

          <h2 id="method-5" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            5. 相続放棄（相続前の場合）
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            まだ相続が発生していない場合、または相続開始から3ヶ月以内であれば、<strong>相続放棄</strong>という選択肢があります。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相続放棄とは
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            相続放棄とは、家庭裁判所に申述することで、最初から相続人でなかったことになる制度です。相続放棄をすると、山林だけでなく、預貯金や不動産などすべての相続財産を相続しないことになります。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相続放棄の重要な注意点
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">①</span>
                山林だけを放棄することはできない
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                相続放棄をすると、相続人は次の順位の相続人に移ります。子が全員放棄すれば親へ、親も放棄すれば兄弟姉妹へ移ります。相続放棄をする場合は、次順位の相続人にも事前に連絡してください。
              </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">④</span>
                相続人全員が放棄した場合
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed mb-4">
                相続人全員が放棄した場合、家庭裁判所に<strong>「相続財産清算人」</strong>の選任を申し立て、清算人に引き渡すことで保存義務から解放されます。
              </p>
              <p className="font-noto text-navy-700 leading-relaxed">
                ただし、選任申立てには<strong>予納金（数十万円〜100万円程度）</strong>がかかります。山林しか財産がない場合、予納金は申立人の自己負担となる可能性が高いです。
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">⑤</span>
                熟慮期間（3ヶ月）に注意
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                相続放棄は、<strong>相続開始を知った時から3ヶ月以内</strong>に家庭裁判所に申述する必要があります。期間を過ぎると原則として放棄できなくなります。
              </p>
            </div>
          </div>

          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-12">
            <h4 className="font-noto font-bold text-navy-900 mb-4 flex items-center">
              <i className="ri-file-list-line text-navy-600 text-xl mr-2"></i>
              山林を相続した場合の届出義務
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              山林（地域森林計画の対象となる森林）を相続した場合、<strong>相続開始から90日以内に市町村に届出をする義務</strong>があります（森林法10条の7の2）。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              届出を怠ると<strong>10万円以下の過料</strong>に処せられます。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              届出先は、山林所在地の市町村の農林担当課です。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            弁護士が行うこと
          </h3>
          <ul className="space-y-3 font-noto text-navy-700 mb-12 ml-6">
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>相続財産全体の調査</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>相続放棄のメリット・デメリット説明</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>家庭裁判所への相続放棄申述書作成・提出</span>
            </li>
            <li className="flex items-start">
              <span className="text-navy-600 mr-3">•</span>
              <span>相続財産管理人選任申立て（必要な場合）</span>
            </li>
          </ul>

          <h2 id="summary" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            まとめ：弁護士に相談するメリット
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            相続した山林を処分する方法として、以下の5つを紹介しました。今後も弊所からは負動産に関する有益な情報を発信いたします。
          </p>

          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-8">
            <ol className="space-y-4 font-noto text-navy-700">
              <li className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">1</span>
                <div>
                  <strong className="text-navy-900">隣接地所有者への譲渡：</strong>
                  <span>最も現実的で成功率が高い</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">2</span>
                <div>
                  <strong className="text-navy-900">相続土地国庫帰属制度：</strong>
                  <span>要件を満たせば国に引き取ってもらえる</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">3</span>
                <div>
                  <strong className="text-navy-900">自治体・公益法人への寄付：</strong>
                  <span>受け入れてもらえるケースは少ない</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">4</span>
                <div>
                  <strong className="text-navy-900">事業者への譲渡：</strong>
                  <span>太陽光発電、資材置き場などの用途がある場合</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">5</span>
                <div>
                  <strong className="text-navy-900">相続放棄：</strong>
                  <span>すべての財産を放棄する覚悟が必要</span>
                </div>
              </li>
            </ol>
          </div>

          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            どの方法が最適かは、山林の状況や他の相続財産の有無によって異なります。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            弁護士に相談することで、あなたの状況に最適な処分方法を見つけることができます。初回相談は無料ですので、まずはお気軽にご相談ください。
          </p>

          {/* 記事本文内のCTAセクションを削除 */}
        </>
      )
    },
    'nouchi-souzoku-5-sentakushi': {
      id: '2',
      title: '農地を相続したくない場合の5つの選択肢｜弁護士が手続きと注意点を解説',
      description: '農地を相続したくない方必見。相続放棄、遺産分割、農地売却、転用、国庫帰属制度の5つの選択肢を弁護士が詳しく解説。農地法の規制と3ヶ月の期限に注意。愛知県対応。初回相談無料。',
      category: '農地',
      tags: ['農地', '相続', '相続放棄', '農地法', '転用'],
      date: '2026-01-20',
      image: 'https://readdy.ai/api/search-image?query=Abandoned%20neglected%20farmland%20agricultural%20field%20with%20overgrown%20weeds%20and%20untended%20crops%2C%20unmaintained%20rural%20agricultural%20land%20showing%20signs%20of%20abandonment%2C%20difficult%20to%20manage%20inherited%20farmland%20with%20wild%20vegetation%2C%20desolate%20agricultural%20landscape%20representing%20problematic%20inherited%20property%20in%20Japan%2C%20realistic%20documentary%20photography%20style%20with%20natural%20overcast%20lighting%20emphasizing%20the%20challenging%20nature%20of%20agricultural%20land%20management&width=1200&height=630&seq=blog-nouchi-abandoned-001&orientation=landscape',
      slug: 'nouchi-souzoku-5-sentakushi',
      content: (
        <>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            「農業をしていないのに農地を相続することになった」<br />
            「相続した農地を持て余している」
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            このようなご相談を受けることが増えています。農地は通常の不動産と異なり、農地法による売買制限があるため、「売れない」「活用できない」「でも固定資産税と管理費用はかかる」という三重苦に陥りがちです。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            本記事では、農地を相続したくない方に向けて、取り得る選択肢とそれぞれのメリット・デメリット、手続き上の注意点を弁護士の視点から解説します。
          </p>

          {/* 目次 */}
          <nav className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-12">
            <h2 className="font-noto text-xl font-bold text-navy-900 mb-4 flex items-center">
              <i className="ri-list-unordered text-navy-600 text-2xl mr-2"></i>
              目次
            </h2>
            <ol className="space-y-3 font-noto text-navy-700">
              <li>
                <a href="#risk" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">1.</span>
                  <span>農地を相続するリスク</span>
                </a>
              </li>
              <li>
                <a href="#options" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">2.</span>
                  <span>農地を相続したくない場合の5つの選択肢</span>
                </a>
              </li>
              <li>
                <a href="#limited" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">3.</span>
                  <span>限定承認という選択肢</span>
                </a>
              </li>
              <li>
                <a href="#consultation" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">4.</span>
                  <span>農地の相続でお困りの方へ</span>
                </a>
              </li>
            </ol>
          </nav>

          <h2 id="risk" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            1. 農地を相続するリスク
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            まず、農地を相続した場合にどのようなリスクがあるかを整理します。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            （1）売却が困難
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            農地を売却するには、<strong>農業委員会の許可（農地法3条許可）</strong>が必要です。許可を得るためには、買主が以下の要件を満たす必要があります。
          </p>
          <div className="bg-beige-50 border-l-4 border-navy-600 p-6 mb-6">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>農地のすべてを効率的に耕作できること</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>必要な農作業に常時従事できること</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>周辺農地の効率的利用に支障を生じないこと</span>
              </li>
            </ul>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            つまり、<strong>農業をする意思と能力のある人にしか売却できません</strong>。不動産会社に依頼しても、買い手が見つからないケースが少なくありません。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            （2）転用にも制限がある
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            農地を宅地や駐車場に転用する場合も、<strong>農地法4条または5条の許可</strong>が必要です。特に、農業振興地域内の農用地区域に指定されている農地（いわゆる<strong>「青地」</strong>）は、原則として転用が認められません。
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-yellow-600 text-xl mr-2"></i>
              青地の転用は二段階の手続きが必要
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              青地を転用するには、まず市町村に<strong>農用地区域からの除外（農振除外）</strong>を申請し、認められた後に農地転用許可を申請するという二段階の手続きが必要になります。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              農振除外自体のハードルが高く、<strong>数年単位の時間を要する</strong>ことも珍しくありません。
            </p>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            立地条件によっては転用許可を得ること自体が極めて困難で、「農地のまま持ち続けるしかない」という状況に陥ることもあります。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            （3）管理の負担と遊休農地問題
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            農地を適切に管理せず放置すると、<strong>農業委員会による利用状況調査・利用意向調査</strong>の対象となり、遊休農地と判断される可能性があります。
          </p>
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3">遊休農地と判断されると</h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>農業委員会から利用意向の確認を受ける</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>農地中間管理機構への貸付け協議の勧告を受けることがある</span>
              </li>
            </ul>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            また、草刈りをせず雑草が繁茂したり害虫が発生したりすると、周辺農地に迷惑をかけ、<strong>近隣トラブル</strong>に発展することもあります。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            遠方に住んでいる場合、管理業者への委託費用も無視できません。固定資産税は農地として低く抑えられているとはいえ、活用できない土地に毎年税金を払い続けることになります。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            （4）相続登記の義務化
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            <strong>令和6年4月1日から相続登記が義務化</strong>されました。不動産を相続で取得したことを知った日から<strong>3年以内</strong>に登記申請が必要です。正当な理由なく怠ると<strong>10万円以下の過料</strong>の対象となります。
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              なお、施行日前に発生した相続についても経過措置として期限が設けられており、<strong>令和9年3月31日まで</strong>に申請が必要です。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              「名義を変えなければ自分のものにならない」というわけではなく、登記をしなくても相続自体は発生しています。<strong>登記を放置しても農地の管理責任や固定資産税の負担からは逃れられません</strong>。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            （5）農業委員会への届出義務
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            相続登記とは別に、<strong>相続等により農地の権利を取得した場合は、農地の所在地の農業委員会への届出が必要</strong>です（農地法3条の3）。
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-12">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-calendar-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span><strong>届出期限：</strong>相続の開始があったことを知った日からおおむね10ヶ月以内</span>
              </li>
              <li className="flex items-start">
                <i className="ri-alert-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span><strong>罰則：</strong>届出をしなかったり虚偽の届出をした場合は、10万円以下の過料に処せられることがあります</span>
              </li>
            </ul>
          </div>

          <h2 id="options" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            2. 農地を相続したくない場合の5つの選択肢
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            では、農地を相続したくない場合にどのような選択肢があるかを見ていきます。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8 flex items-center">
            <span className="bg-navy-700 text-white w-10 h-10 rounded-full flex items-center justify-center mr-3 text-lg">1</span>
            相続放棄
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            最も確実に農地の相続を回避できる方法です。
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-file-text-line text-navy-600 mr-2"></i>
                手続き
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                被相続人の最後の住所地を管轄する家庭裁判所に「相続放棄申述書」を提出します。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-calendar-line text-navy-600 mr-2"></i>
                期限
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                原則として、自己のために相続の開始があったことを知った時から<strong>3ヶ月以内</strong>です。
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-information-line text-blue-600 text-xl mr-2"></i>
              熟慮期間の起算点について
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              判例上、熟慮期間は原則として、被相続人の死亡の事実を知り、かつ、これにより自分が法律上の相続人となった事実を知った時から起算されます。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              したがって、<strong>先順位の相続人全員が相続放棄をしたことで自分が相続人となった場合は、その事実を知った時点から3ヶ月</strong>となります。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              また、例外的に、相続人において相続財産が全く存在しないと信じ、そう信じることに相当な理由があり、調査を期待することが著しく困難であった等の特段の事情がある場合には、相続財産の全部または一部の存在を認識した時（または通常これを認識し得べき時）から起算するとした判例もあります。遠方の農地の存在を知らなかったケースなどでは、この例外が問題になり得ます。
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-time-line text-yellow-600 text-xl mr-2"></i>
              熟慮期間の伸長
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed">
              3ヶ月以内に判断できない事情がある場合は、家庭裁判所に<strong>「熟慮期間伸長の申立て」</strong>をすることで期間を延長できます。農地の調査や他の相続財産の把握に時間がかかる場合などは、期間内にこの申立てを検討すべきです。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-check-line text-green-600 text-xl mr-2"></i>
                メリット
              </h4>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>農地を含むすべての相続債務・相続財産から解放される</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>手続きが比較的シンプル</span>
                </li>
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-close-line text-red-500 text-xl mr-2"></i>
                デメリット
              </h4>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>農地を含むすべての相続債務・相続財産から解放される</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>手続きが比較的シンプル</span>
                </li>
              </ul>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-12 flex items-center">
            <span className="bg-navy-700 text-white w-10 h-10 rounded-full flex items-center justify-center mr-3 text-lg">2</span>
            他の相続人に取得してもらう（遺産分割協議）
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            相続人が複数いる場合、遺産分割協議で農地を他の相続人に取得してもらうことが考えられます。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            農業を継続する意思のある相続人がいれば、その方に農地を集約することは合理的です。その代わり、他の財産（預貯金など）を多く取得するという調整も可能です。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            ただし、現実には<strong>「誰も農地を欲しがらない」</strong>というケースが多いです。
          </p>

          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-file-list-line text-navy-600 mr-2"></i>
              手続き
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              売買契約を締結した上で、農業委員会に<strong>農地法3条許可申請</strong>を行います。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              許可が下りなければ売買は無効となりますので、契約書には<strong>「農地法の許可を条件とする」</strong>旨を明記しておくべきです。
            </p>
          </div>

          <h4 className="font-noto text-xl font-bold text-navy-900 mb-4 mt-6">
            買い手の探し方
          </h4>
          <div className="space-y-3 mb-6">
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>農業委員会のあっせん制度を利用する</span>
              </p>
            </div>
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>農地中間管理機構（農地バンク）に相談する</span>
              </p>
            </div>
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>JAや地元の農業者に声をかける</span>
              </p>
            </div>
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>農地専門の不動産業者に依頼する</span>
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-money-yen-circle-line text-yellow-600 text-xl mr-2"></i>
              注意点
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed">
              農地の売却価格は宅地と比べて非常に低いのが一般的です。固定資産税評価額を下回るケースも珍しくありません。「売れるだけでもありがたい」という気持ちで臨む必要があるかもしれません。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-12 flex items-center">
            <span className="bg-navy-700 text-white w-10 h-10 rounded-full flex items-center justify-center mr-3 text-lg">3</span>
            転用して売却・活用する
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            農地転用が可能な土地であれば、宅地等に転用した上で売却または活用する選択肢があります。
          </p>

          <h4 className="font-noto text-xl font-bold text-navy-900 mb-4 mt-6">
            農地転用の許可基準
          </h4>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            農地転用の許可基準には<strong>「立地基準」</strong>と<strong>「一般基準」</strong>があります。
          </p>

          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-6">
            <h5 className="font-noto font-bold text-navy-900 mb-4">立地基準による区分</h5>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start">
                  <span className="bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">原則不許可</span>
                  <div>
                    <p className="font-noto font-bold text-navy-900 mb-1">農用地区域内農地（青地）</p>
                    <p className="font-noto text-navy-700 text-sm">転用には農振除外が前提</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start">
                  <span className="bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">原則不許可</span>
                  <div>
                    <p className="font-noto font-bold text-navy-900 mb-1">甲種農地・第1種農地</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start">
                  <span className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">条件付許可</span>
                  <div>
                    <p className="font-noto font-bold text-navy-900 mb-1">第2種農地</p>
                    <p className="font-noto text-navy-700 text-sm">周辺に代替地がない場合等に許可</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">原則許可</span>
                  <div>
                    <p className="font-noto font-bold text-navy-900 mb-1">第3種農地</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-information-line text-blue-600 text-xl mr-2"></i>
              市街化区域内の農地
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              市街化区域内の農地については、農地法上の許可は不要で、<strong>あらかじめ農業委員会への届出</strong>を行えば転用が可能です（届出は事前に行う必要があります）。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              ただし、都市計画法や建築基準法など他の法令による規制は別途適用されます。
            </p>
          </div>

          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            まずは農業委員会に相談し、所有する農地がどの区分に該当するかを確認してください。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相続土地国庫帰属制度を利用する
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            令和5年4月27日から、相続した土地を国に引き取ってもらえる<strong>「相続土地国庫帰属制度」</strong>が始まりました。農地も対象となります。
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-building-line text-navy-600 mr-2"></i>
                申請先
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                土地の所在地を管轄する法務局
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-money-yen-circle-line text-navy-600 mr-2"></i>
                費用
              </h4>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li><strong>審査手数料：</strong>土地1筆につき14,000円</li>
                <li><strong>負担金：</strong>原則20万円（農地は面積等に応じて算定）</li>
              </ul>
            </div>
          </div>

          <h4 className="font-noto text-xl font-bold text-navy-900 mb-4 mt-6">
            国庫帰属が認められない主な要件
          </h4>
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>建物がある土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>担保権や使用収益権が設定されている土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>境界が明らかでない土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>土壌汚染対策法上の特定有害物質による汚染がある土地</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-1"></i>
                <span>通路など他人の利用が予定されている土地</span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-yellow-600 text-xl mr-2"></i>
              農地特有の注意点
            </h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>水利権や入会権などの権利関係が設定されている場合は対象外となり得ます</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>土壌汚染については、農薬であっても特定有害物質に該当する種類・濃度であれば問題となる可能性があるため、事前に確認が必要です</span>
              </li>
            </ul>
          </div>

          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            この制度は、売却も転用もできない農地の<strong>「最後の手段」</strong>として有効ですが、要件が厳しく、すべての農地が引き取ってもらえるわけではありません。法務局の相談窓口で事前に確認することをお勧めします。
          </p>

          <h2 id="limited" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            3. 限定承認という選択肢
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            あまり知られていませんが、<strong>「限定承認」</strong>という方法もあります。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            限定承認とは、<strong>相続財産の範囲内でのみ被相続人の債務を弁済し、残余があれば相続する</strong>という手続きです。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            農地の価値が不明な場合や、他の相続財産・債務との兼ね合いで相続すべきか判断がつかない場合に有効な選択肢となり得ます。
          </p>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-yellow-600 text-xl mr-2"></i>
              限定承認の制約
            </h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span><strong>相続人全員で共同して行う必要がある</strong>（単独ではできない）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>家庭裁判所での手続きが必要で、相続財産の清算手続きを行う必要がある</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <span>被相続人から相続人への譲渡があったものとみなされ、譲渡所得税が課される可能性がある</span>
              </li>
            </ul>
          </div>

          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            実務上、限定承認が利用されるケースは多くありませんが、状況によっては検討に値します。
          </p>

          <h2 id="consultation" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            4. 農地の相続でお困りの方へ
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            農地の相続問題は、<strong>農地法の規制と相続法の両方が絡む複雑な問題</strong>です。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            「相続放棄すべきか」「遺産分割でどう主張すべきか」「転用は可能か」といった判断は、個別の事情によって大きく異なります。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            当事務所では、農地を含む相続問題について法律相談を承っております。愛知県内の農地については、地域の農業委員会の運用も踏まえてアドバイスいたします。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            お気軽にご相談ください。
          </p>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-amber-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                負動産でお困りの方へ
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                初回相談は無料です。お気軽にご相談ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="outline"
                  size="lg"
                  href="https://forms.gle/zPw2NXXr6K7nXgAm6"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFormClick}
                  className="flex items-center justify-center gap-3 bg-white text-amber-600 hover:bg-gray-50 border-amber-600 shadow-lg"
                >
                  <i className="ri-mail-heart-line text-xl"></i>
                  メールで初回相談の予約
                </Button>
                
                <Button
                  variant="secondary"
                  size="lg"
                  href="https://lin.ee/34dhahY"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLineClick}
                  className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white border-green-500 shadow-lg"
                >
                  <i className="ri-line-fill text-xl"></i>
                  LINEで初回相談の予約
                </Button>
              </div>
              <p className="font-noto text-sm text-gray-600 mt-6 flex items-center justify-center">
                <i className="ri-time-line mr-2"></i>
                3営業日以内に心を込めてお返事いたします
              </p>
            </div>
          </section>
        </>
      )
    },
    'kokko-kizon-shinsei-houhou': {
      id: '3',
      title: '相続土地国庫帰属制度を自分で申請する方法｜手続きの流れと必要書類を解説',
      description: '相続土地国庫帰属制度の申請手順を弁護士が徹底解説。事前相談から承認まで、必要書類、審査手数料14,000円、負担金20万円〜の詳細。自分で申請する方法と専門家に依頼するメリットを比較。',
      category: '法律知識',
      tags: ['国庫帰属', '相続', '手続き', '法務局', '負担金'],
      date: '2026-01-25',
      image: 'https://static.readdy.ai/image/5e182d52a94dfeeda6703180d188c585/7c76bb1e5aefffc2cf36a7486d810c54.jpeg',
      slug: 'kokko-kizon-shinsei-houhou',
      content: (
        <>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            相続で土地を取得したものの、使う予定もなく、固定資産税や管理の手間だけがかかっている。そんな悩みを抱えている方は少なくありません。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            令和5年4月から始まった<strong>「相続土地国庫帰属制度」</strong>は、一定の要件を満たす土地を国に引き取ってもらえる制度です。この記事では、制度を利用するための具体的な手順を、準備段階から完了まで順を追って解説します。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            手続きの全体的な流れは、<strong>「事前相談」→「承認申請」→「審査（書面・実地）」→「承認・負担金の納付」→「国庫への帰属（手続き完了）」</strong>という5つのステップに分かれます。
          </p>

          {/* 目次 */}
          <nav className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-12">
            <h2 className="font-noto text-xl font-bold text-navy-900 mb-4 flex items-center">
              <i className="ri-list-unordered text-navy-600 text-2xl mr-2"></i>
              目次
            </h2>
            <ol className="space-y-3 font-noto text-navy-700">
              <li>
                <a href="#step-1" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">1.</span>
                  <span>事前の準備・相談</span>
                </a>
              </li>
              <li>
                <a href="#step-2" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">2.</span>
                  <span>承認申請（書類の提出）</span>
                </a>
              </li>
              <li>
                <a href="#step-3" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">3.</span>
                  <span>審査（法務局による調査）</span>
                </a>
              </li>
              <li>
                <a href="#step-4" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">4.</span>
                  <span>承認と負担金の納付</span>
                </a>
              </li>
              <li>
                <a href="#step-5" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">5.</span>
                  <span>国庫への帰属（手続き完了）</span>
                </a>
              </li>
              <li>
                <a href="#summary" className="hover:text-navy-900 hover:underline cursor-pointer flex items-start">
                  <span className="mr-2">6.</span>
                  <span>まとめ</span>
                </a>
              </li>
            </ol>
          </nav>

          <h2 id="step-1" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            1. 事前の準備・相談
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            申請を行う前に、まず自分の土地が制度の対象になるかどうかを確認する必要があります。法務局では事前相談を受け付けており、申請前に利用することが推奨されています。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相談窓口
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            相談は、<strong>土地の所在地を管轄する法務局・地方法務局の本局</strong>で受け付けています。支局や出張所では原則として対応していない点に注意が必要です。
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-map-pin-line text-blue-600 text-xl mr-2"></i>
              名古屋法務局の場合
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed">
              本局は名古屋市中区三の丸にあります。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            相談方法
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            相談方法は<strong>対面または電話</strong>で、いずれも<strong>予約制</strong>となっています。1回あたり30分程度の枠が設けられていますので、法務局のウェブサイトから予約を取ってください。
          </p>
          <div className="bg-beige-50 border-l-4 border-navy-600 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3">相談できる人</h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>土地の所有者本人</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>その家族や親族</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>弁護士や司法書士などの専門家（同席可能）</span>
              </li>
            </ul>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            準備する資料
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            相談の際には、以下の資料を準備しておくとスムーズです。
          </p>
          <div className="space-y-4 mb-12">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">①</span>
                相談対応用チェックシート
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                法務省のウェブサイトで公開されている「相談対応用チェックシート」をあらかじめ記入しておくとスムーズです。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">②</span>
                土地の登記事項証明書
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                法務局で取得できます（1通600円）。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">③</span>
                公図
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                土地の位置関係がわかる図面です（1通450円）。
              </p>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">④</span>
                現地の写真
              </h4>
              <p className="font-noto text-navy-700 leading-relaxed">
                土地の状況がわかる写真があると、より具体的なアドバイスを受けることができます。
              </p>
            </div>
          </div>

          <h2 id="step-2" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            2. 承認申請（書類の提出）
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            事前相談を経て申請することを決めたら、必要書類を作成して提出します。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            申請できる人
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            申請できるのは、<strong>相続または遺贈（相続人に対する遺贈に限る）によって土地を取得した所有者本人</strong>です。
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-yellow-600 text-xl mr-2"></i>
              共有地の場合の注意点
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              土地が共有になっている場合は、<strong>共有者全員で共同して申請</strong>しなければなりません。一部の共有者だけで申請することはできないため、他の共有者との事前調整が必要になります。
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              なお、弁護士、司法書士、行政書士に書類作成を依頼することは可能ですが、申請者（名義人）はあくまで所有者本人となります。
            </p>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            提出先
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            <strong>土地の所在地を管轄する法務局・地方法務局の本局</strong>です。窓口への持参のほか、郵送での提出も認められています。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            必要書類
          </h3>
          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-8">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-file-text-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span><strong>承認申請書</strong>（法務省ホームページの様式を使用）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-map-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span><strong>土地の位置及び範囲を明らかにする図面</strong>（公図や地図など）</span>
              </li>
              <li className="flex items-start">
                <i className="ri-camera-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span><strong>土地と隣接地との境界点を明らかにする写真</strong></span>
              </li>
              <li className="flex items-start">
                <i className="ri-image-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span><strong>土地の形状を明らかにする写真</strong></span>
              </li>
              <li className="flex items-start">
                <i className="ri-user-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span><strong>申請者の住所に変更がある場合などは、氏名・住所を証する書面</strong></span>
              </li>
            </ul>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            登記事項証明書（いわゆる登記簿謄本）の提出は原則として不要とされていますが、事前相談の段階では持参しておいたほうがよいでしょう。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            審査手数料
          </h3>
          <div className="bg-white border border-beige-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-noto font-bold text-navy-900 flex items-center">
                <i className="ri-money-yen-circle-line text-navy-600 text-xl mr-2"></i>
                手数料
              </h4>
              <p className="font-noto text-2xl font-bold text-navy-900">
                14,000円<span className="text-base font-normal text-navy-600">/筆</span>
              </p>
            </div>
            <p className="font-noto text-navy-700 text-sm">
              申請の際には、審査手数料として土地1筆につき14,000円分の収入印紙を申請書に貼り付けて納付します。
            </p>
          </div>
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-12">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-alert-line text-red-500 text-xl mr-2"></i>
              重要な注意点
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed">
              この手数料は、<strong>審査の結果として却下や不承認となった場合でも返還されません</strong>ので、事前相談の段階で要件を満たしているかどうかをしっかり確認しておくことが重要です。
            </p>
          </div>

          <h2 id="step-3" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            3. 審査（法務局による調査）
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            申請が受理されると、法務局による審査が行われます。審査期間は<strong>おおむね半年から1年程度</strong>と見込まれています。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            審査の流れ
          </h3>
          <div className="space-y-4 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">1</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">書面調査</h4>
                  <p className="font-noto text-navy-700 text-sm">提出書類と登記記録などの公的記録との照合が行われます。</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">2</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">関係機関への照会</h4>
                  <p className="font-noto text-navy-700 text-sm">土地が農地であれば農業委員会に、森林であれば森林整備部局に、それぞれ土地の管理状況や利用予定についての情報提供が求められます。</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">3</span>
                <div>
                  <h4 className="font-noto font-bold text-navy-900 mb-2">実地調査</h4>
                  <p className="font-noto text-navy-700 text-sm">法務局の担当官が実際に現地を訪問し、土地の現状を確認します。申請者の立ち会いは原則として不要ですが、土地の場所がわかりにくい場合などには同行を求められることがあります。</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-12">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-information-line text-blue-600 text-xl mr-2"></i>
              申請の取下げ
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed">
              承認通知が届く前であれば、いつでも申請を取り下げることができます。審査の途中で事情が変わった場合には、取下げを検討することも可能です。
            </p>
          </div>

          <h2 id="step-4" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            4. 承認と負担金の納付
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            審査の結果、要件を満たしていると判断されると、法務大臣（実際には法務局長）から承認の通知が届きます。
          </p>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            通知書類
          </h3>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-file-check-line text-green-600 mr-2"></i>
                承認の場合
              </h4>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li>• 承認通知書</li>
                <li>• 負担金納付告知書</li>
              </ul>
            </div>
            <div className="bg-white border border-beige-200 rounded-lg p-6">
              <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
                <i className="ri-file-close-line text-red-500 mr-2"></i>
                不承認の場合
              </h4>
              <p className="font-noto text-navy-700 text-sm">
                理由を付した不承認通知が届きます
              </p>
            </div>
          </div>

          <h3 className="font-noto text-2xl font-bold text-navy-900 mb-4 mt-8">
            負担金の納付
          </h3>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-4">
            承認を受けた場合、負担金を納付する必要があります。
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-time-line text-yellow-600 text-xl mr-2"></i>
              納付期限
            </h4>
            <p className="font-noto text-navy-700 leading-relaxed mb-3">
              <strong>負担金納付告知書が届いた翌日から30日以内</strong>
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              この期限は厳格に適用されますので、期限内に納付しなかった場合は承認の効力が失われてしまいます。承認がなかったことになってしまうため、通知が届いたらすぐに対応するようにしてください。
            </p>
          </div>

          <h4 className="font-noto text-xl font-bold text-navy-900 mb-4 mt-6">
            負担金の金額
          </h4>
          <div className="bg-white border border-beige-200 rounded-lg p-6 mb-6">
            <p className="font-noto text-navy-700 leading-relaxed mb-4">
              <strong>原則：20万円</strong>
            </p>
            <p className="font-noto text-navy-700 leading-relaxed">
              ただし、一部の市街化区域内の宅地や農地、森林などについては、<strong>面積に応じて算定</strong>されるため、20万円より高額になることがあります。
            </p>
          </div>

          <h4 className="font-noto text-xl font-bold text-navy-900 mb-4 mt-6">
            納付方法
          </h4>
          <div className="space-y-3 mb-12">
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-bank-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>日本銀行の歳入代理店（銀行や郵便局など）の窓口で納付</span>
              </p>
            </div>
            <div className="bg-beige-50 border-l-4 border-navy-600 p-4">
              <p className="font-noto text-navy-700 flex items-start">
                <i className="ri-smartphone-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>インターネットバンキング（Pay-easy）を利用して納付</span>
              </p>
            </div>
          </div>

          <h2 id="step-5" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            5. 国庫への帰属（手続き完了）
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            <strong>負担金を納付した時点で、土地の所有権が国に移転します。</strong>
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-8">
            国への所有権移転登記は、<strong>国（法務局）が職権で行います</strong>ので、申請者が自分で登記申請をする必要はありません。
          </p>
          <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-12">
            <h4 className="font-noto font-bold text-navy-900 mb-3 flex items-center">
              <i className="ri-check-double-line text-green-600 text-xl mr-2"></i>
              手続き完了後
            </h4>
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>土地の管理義務から解放される</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <span>固定資産税の負担もなくなる</span>
              </li>
            </ul>
          </div>

          <h2 id="summary" className="font-noto text-3xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-beige-200">
            まとめ
          </h2>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            相続土地国庫帰属制度は、手続き自体は自分で行うことも可能です。
          </p>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-6">
            ただし、以下の点を考慮すると、専門家に相談したほうが安心という方も多いでしょう。
          </p>
          <div className="bg-beige-50 border border-beige-200 rounded-lg p-6 mb-8">
            <ul className="space-y-3 font-noto text-navy-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>事前相談から承認まで<strong>半年から1年程度の期間</strong>がかかる</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>書類の準備や現地写真の撮影など<strong>一定の手間</strong>がかかる</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-line text-navy-600 text-xl mr-3 mt-1"></i>
                <span>申請前に<strong>「この土地が本当に要件を満たすのか」を正確に判断</strong>する必要がある</span>
              </li>
            </ul>
          </div>
          <p className="font-noto text-lg text-navy-700 leading-relaxed mb-12">
            当事務所では、相続土地国庫帰属制度の申請サポートを行っております。「自分の土地が対象になるか知りたい」「書類の作成を任せたい」という方は、お気軽にご相談ください。
          </p>
        </>
      )
    }
  };

  const article = slug ? articles[slug] : null;

  useEffect(() => {
    if (article) {
      document.title = `${article.title}｜冨田・島岡法律事務所`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', article.description);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* シンプルなヘッダー */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center cursor-pointer">
              <span className="font-noto text-xl font-bold text-navy-800">冨田・島岡法律事務所</span>
            </Link>
          </div>
        </header>
        <main className="pt-12 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="font-noto text-3xl font-bold text-navy-900 mb-4">
              記事が見つかりません
            </h1>
            <Link
              to="/blog"
              className="inline-flex items-center text-navy-600 hover:text-navy-800 font-noto cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              コラム一覧に戻る
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": {
      "@type": "ImageObject",
      "url": article.image,
      "width": 1200,
      "height": 630
    },
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "冨田・島岡法律事務所",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "冨田・島岡法律事務所",
      "logo": {
        "@type": "ImageObject",
        "url": siteUrl + "/logo.png",
        "width": 200,
        "height": 60
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": siteUrl + "/blog/" + article.slug
    },
    "keywords": article.tags.join(", "),
    "articleSection": article.category,
    "inLanguage": "ja-JP"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "ホーム",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "コラム",
        "item": siteUrl + "/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.category,
        "item": siteUrl + "/blog?category=" + encodeURIComponent(article.category)
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": article.title,
        "item": siteUrl + "/blog/" + article.slug
      }
    ]
  };

  const webPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": article.title,
    "description": article.description,
    "url": siteUrl + "/blog/" + article.slug,
    "isPartOf": {
      "@type": "WebSite",
      "name": "冨田・島岡法律事務所",
      "url": siteUrl
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", ".article-summary"]
    }
  };

  return (
    <>
      <StructuredData data={articleStructuredData} />
      <StructuredData data={breadcrumbData} />
      <StructuredData data={webPageData} />
      <div className="min-h-screen bg-white">
        {/* シンプルなヘッダー（フローティングバーなし） */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex flex-col cursor-pointer">
              <span className="font-noto text-xl font-bold text-navy-800">冨田・島岡法律事務所</span>
              <span className="font-en text-[10px] tracking-wider text-navy-600">TOMIDA SHIMAOKA LAW OFFICE</span>
            </Link>
            <Link to="/blog" className="text-navy-600 hover:text-navy-800 font-noto text-sm cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i>
              コラム一覧
            </Link>
          </div>
        </header>
        
        <main className="pt-8 pb-20">
          {/* パンくずリスト */}
          <div className="max-w-4xl mx-auto px-6 mb-8">
            <nav className="flex items-center space-x-2 text-sm font-noto text-navy-600">
              <Link to="/" className="hover:text-navy-800 cursor-pointer">ホーム</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-navy-800 cursor-pointer">コラム</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-navy-400">{article.category}</span>
            </nav>
          </div>

          {/* 記事ヘッダー */}
          <article className="max-w-4xl mx-auto px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1 bg-beige-100 text-navy-700 text-sm font-medium rounded-full">
                  {article.category}
                </span>
                <time className="text-navy-500 text-sm">
                  {new Date(article.date).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              <h1 className="font-noto text-3xl md:text-4xl font-bold text-navy-900 mb-6 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="text-sm text-navy-600 bg-beige-50 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* アイキャッチ画像 */}
            <div className="w-full h-96 mb-12 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* 記事本文 */}
            <div className="prose prose-lg max-w-none">
              {article.content}
            </div>

            {/* 記事フッター */}
            <div className="mt-16 pt-8 border-t border-beige-200">
              <div className="bg-gradient-to-r from-navy-700 to-navy-600 text-white rounded-xl p-8 text-center mb-8">
                <h3 className="font-noto text-2xl font-bold mb-4">
                  {article.category === '農地' ? '農地の相続でお困りの方へ' : '相続した山林の処分でお困りの方へ'}
                </h3>
                <p className="font-noto text-lg mb-6">
                  初回法律相談は無料です。まずはお気軽にご相談ください。
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    href="mailto:shin.kato@tomidashimaoka.com?subject=【相談】負動産の法的処分&body=お名前：%0D%0Aご連絡先：%0D%0A物件種別（山林/農地/空き家）：%0D%0A所在地市町村：%0D%0A筆数・目安面積：%0D%0A現況（写真があれば添付可）：%0D%0Aご相談内容："
                    onClick={handleMailtoClick}
                    className="flex items-center justify-center gap-3 bg-white text-navy-700 hover:bg-gray-50 border-amber-600 shadow-lg"
                  >
                    <i className="ri-mail-line text-xl mr-2"></i>
                    メールで初回相談の予約
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    href="https://lin.ee/34dhahY"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLineClick}
                    className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white border-green-500 shadow-lg"
                  >
                    <i className="ri-line-fill text-xl mr-2"></i>
                    LINEで初回相談の予約
                  </Button>
                </div>
              </div>

              <Link
                to="/blog"
                className="inline-flex items-center text-navy-600 hover:text-navy-800 font-noto font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl mr-2"></i>
                コラム一覧に戻る
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticle;